// src/vatandas/pages/VatandasCreateApplicationPage.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createApplication,
  fetchOtpConfig,
  initOtpSession,
  resendOtp,
  verifyOtpAndSubmit,
  resetOtpState,
  clearOtpError,
} from "../redux/slices/vatandasApplicationSlice";

import "./CreateApplicationPage.css";
import PreciseLocationPicker from "../components/location/PreciseLocationPicker";

import {
  UserIcon,
  PhoneIcon,
  EnvelopeIcon,
  TagIcon,
  PencilSquareIcon,
  DocumentPlusIcon,
  Squares2X2Icon,
  MapPinIcon,
  PaperClipIcon,
  DocumentTextIcon,
  TrashIcon,
  ShieldCheckIcon,
  ArrowUpTrayIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  DocumentDuplicateIcon,
  DevicePhoneMobileIcon,
  ClockIcon,
  ArrowPathRoundedSquareIcon,
  LockClosedIcon,
} from "@heroicons/react/24/outline";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8001/api";

/* =============================================================================
  Helpers — DEĞİŞMEDİ
============================================================================= */

const validatePhoneNumber = (phone) => /^[1-9]\d{9}$/.test(String(phone || "").trim());
const validateEmail = (email) => {
  if (!email) return true;
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(String(email).trim());
};

function safeRound6(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return Number(n.toFixed(6));
}

function normalizeListPayload(data) {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.results)) return data.results;
  return [];
}

function humanFileSize(bytes) {
  const n = Number(bytes || 0);
  if (!Number.isFinite(n) || n <= 0) return "";
  if (n < 1024) return `${n} B`;
  const kb = n / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(1)} MB`;
}

function formatRemaining(totalSeconds) {
  const n = Math.max(0, Number(totalSeconds || 0));
  const mm = String(Math.floor(n / 60)).padStart(2, "0");
  const ss = String(n % 60).padStart(2, "0");
  return `${mm}:${ss}`;
}

function maskPhone(phone) {
  const raw = String(phone || "").replace(/\D/g, "");
  if (raw.length !== 10) return phone || "-";
  return `${raw.slice(0, 3)} *** ** ${raw.slice(-2)}`;
}

async function copyToClipboard(text) {
  const s = String(text || "");
  if (!s) return false;

  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(s);
      return true;
    }
  } catch {}

  try {
    const el = document.createElement("textarea");
    el.value = s;
    el.setAttribute("readonly", "");
    el.style.position = "fixed";
    el.style.left = "-9999px";
    document.body.appendChild(el);
    el.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(el);
    return ok;
  } catch {
    return false;
  }
}

/* =============================================================================
  UI Pieces — Toast, SuccessModal, KvkkModal DEĞİŞMEDİ
============================================================================= */

function Toast({ toast, onClose }) {
  if (!toast) return null;

  const Icon =
    toast.type === "success"
      ? CheckCircleIcon
      : toast.type === "info"
      ? ExclamationTriangleIcon
      : ExclamationTriangleIcon;

  return (
    <div className={`vca-toast ${toast.type}`} role="status" aria-live="polite">
      <Icon className="vca-toast-ic" aria-hidden="true" />
      <div className="vca-toast-body">
        <div className="vca-toast-title">{toast.title}</div>
        <div className="vca-toast-text">{toast.message}</div>
      </div>
      <button className="vca-toast-close" onClick={onClose} aria-label="Kapat">
        <XMarkIcon className="vca-toast-close-ic" />
      </button>
    </div>
  );
}

function SuccessModal({ open, trackingNumber, onClose, onCopy }) {
  if (!open) return null;

  return (
    <>
      <div className="vca-modal-overlay" onClick={onClose} />
      <div className="vca-modal" role="dialog" aria-modal="true" aria-label="Başvuru gönderildi">
        <div className="vca-modal-card v-card">
          <div className="vca-modal-head">
            <div className="vca-modal-badge" aria-hidden="true">
              <CheckCircleIcon className="vca-modal-badge-ic" />
            </div>

            <div className="vca-modal-title-wrap">
              <div className="vca-modal-title">Başvurunuz başarıyla oluşturuldu</div>
              <div className="vca-modal-sub">
                Başvurunuz en kısa sürede çözüme ulaştırılacaktır. Takip numaranızı saklayın.
              </div>
            </div>

            <button className="vca-modal-x" onClick={onClose} aria-label="Kapat">
              <XMarkIcon className="vca-modal-x-ic" />
            </button>
          </div>

          <div className="vca-modal-body">
            <div className="vca-track">
              <div className="vca-track-label">Takip Numaranız</div>

              <div className="vca-track-row">
                <div className="vca-track-value">{trackingNumber || "-"}</div>

                <button
                  type="button"
                  className="vca-track-copy"
                  onClick={onCopy}
                  disabled={!trackingNumber}
                  aria-label="Takip numarasını kopyala"
                  title="Kopyala"
                >
                  <DocumentDuplicateIcon className="vca-track-copy-ic" aria-hidden="true" />
                </button>
              </div>

              <div className="vca-track-hint">Bu pencere 10 saniye sonra kapanır.</div>
            </div>
          </div>

          <div className="vca-modal-actions">
            <button type="button" className="v-btn v-btn--primary" onClick={onClose}>
              Tamam
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function KvkkModal({ open, html, onClose, onAccept }) {
  if (!open) return null;

  return (
    <>
      <div className="vca-modal-overlay" onClick={onClose} />
      <div className="vca-modal" role="dialog" aria-modal="true" aria-label="KVKK Metni">
        <div className="vca-modal-card v-card">
          <div className="vca-modal-head">
            <div className="vca-modal-badge" aria-hidden="true">
              <ShieldCheckIcon className="vca-modal-badge-ic" />
            </div>

            <div className="vca-modal-title-wrap">
              <div className="vca-modal-title">KVKK Metni</div>
              <div className="vca-modal-sub">Lütfen metni okuyup onaylayınız.</div>
            </div>

            <button className="vca-modal-x" onClick={onClose} aria-label="Kapat">
              <XMarkIcon className="vca-modal-x-ic" />
            </button>
          </div>

          <div className="vca-modal-body vca-kvkk-body">
            <div dangerouslySetInnerHTML={{ __html: html || "" }} />
          </div>

          <div className="vca-modal-actions">
            <button type="button" className="v-btn" onClick={onClose}>
              Kapat
            </button>
            <button type="button" className="v-btn v-btn--primary" onClick={onAccept}>
              Kabul Ediyorum
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

/* =============================================================================
  OtpModal — YENİDEN TASARLANDI
  - Tüm props aynı, sadece görsel yapı değişti
  - Hata mesajları kullanıcı dostu (kod bilgisi yok)
  - Apple tarzı minimal kart tasarımı
  - Hem mobil hem web için optimize
============================================================================= */

/**
 * Hata mesajını kullanıcıya gösterilebilecek sade metne dönüştürür.
 * Teknik detay, stack trace veya kod parçacığı içeren metinleri temizler.
 */
function sanitizeErrorMessage(raw) {
  if (!raw) return null;
  const s = String(raw).trim();
  if (!s) return null;

  // Teknik hata kalıplarını yakala — kullanıcıya gösterme
  const technicalPatterns = [
    /^Error:/i,
    /^TypeError/i,
    /^ValueError/i,
    /^Exception/i,
    /at\s+\w+\s*\(/,          // stack trace satırı
    /File\s+".*",\s+line\s+\d+/i,
    /Traceback/i,
    /django\./i,
    /rest_framework\./i,
    /notifications_app\./i,
    /core_app\./i,
    /^\s*\{/,                 // JSON objesi
    /^\s*\[/,                 // JSON dizisi
    /[<>]/,                   // HTML tag veya comparison
    /\bimport\b/,
    /\bdef\b\s+\w+\s*\(/,
    /\bclass\b\s+\w+/,
  ];

  for (const pattern of technicalPatterns) {
    if (pattern.test(s)) {
      return "İşlem sırasında bir sorun oluştu. Lütfen tekrar deneyin.";
    }
  }

  // 200 karakterden uzunsa kırp
  if (s.length > 200) {
    return s.slice(0, 200) + "…";
  }

  return s;
}

function OtpModal({
  open,
  phoneNumber,
  code,
  onCodeChange,
  onVerify,
  onResend,
  onClose,
  expiresInSec,
  resendInSec,
  smsRemaining,
  loading,
  errorText,
}) {
  if (!open) return null;

  const canResend = resendInSec <= 0 && !loading && smsRemaining > 0;
  const masked = maskPhone(phoneNumber);
  const safeError = sanitizeErrorMessage(errorText);

  // Süre rengi: kırmızıya doğru geçiş
  const timerCritical = expiresInSec > 0 && expiresInSec <= 60;
  const timerDead = expiresInSec === 0;

  return (
    <>
      <div
        className="vca-modal-overlay vca-otp-overlay"
        onClick={loading ? undefined : onClose}
      />
      <div
        className="vca-modal vca-otp-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="SMS doğrulama"
      >
        <div className="vca-otp-card">

          {/* ── Başlık satırı ── */}
          <div className="vca-otp-head">
            <div className="vca-otp-head-icon" aria-hidden="true">
              <DevicePhoneMobileIcon />
            </div>
            <div className="vca-otp-head-text">
              <div className="vca-otp-title">SMS Doğrulama</div>
              <div className="vca-otp-sub">
                <span className="vca-otp-phone">{masked}</span> numarasına gönderildi
              </div>
            </div>
            <button
              className="vca-otp-close"
              onClick={onClose}
              disabled={loading}
              aria-label="Kapat"
            >
              <XMarkIcon />
            </button>
          </div>

          {/* ── Hata bandı ── */}
          {safeError ? (
            <div className="vca-otp-error" role="alert">
              <ExclamationTriangleIcon className="vca-otp-error-ic" aria-hidden="true" />
              <span>{safeError}</span>
            </div>
          ) : null}

          {/* ── Kod girişi ── */}
          <div className="vca-otp-body">
            <label className="vca-otp-code-label" htmlFor="otp-input">
              6 Haneli Doğrulama Kodu
            </label>
            <div className="vca-otp-code-wrap">
              <LockClosedIcon className="vca-otp-code-ic" aria-hidden="true" />
              <input
                id="otp-input"
                className="vca-otp-code-input"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(e) =>
                  onCodeChange(e.target.value.replace(/\D/g, "").slice(0, 6))
                }
                placeholder="• • • • • •"
                disabled={loading}
                autoFocus
              />
              {String(code || "").length === 6 && !loading ? (
                <CheckCircleIcon className="vca-otp-code-check" aria-hidden="true" />
              ) : null}
            </div>
          </div>

          {/* ── Durum paneli ── */}
          <div className="vca-otp-info">

            {/* Süre */}
            <div
              className={[
                "vca-otp-info-row",
                timerCritical ? "vca-otp-info-row--warn" : "",
                timerDead ? "vca-otp-info-row--dead" : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              <ClockIcon className="vca-otp-info-ic" aria-hidden="true" />
              <span className="vca-otp-info-key">Oturum süresi</span>
              <span className="vca-otp-info-val">
                {timerDead ? (
                  <span className="vca-otp-expired-badge">Süresi doldu</span>
                ) : (
                  <span className="vca-otp-timer">{formatRemaining(expiresInSec)}</span>
                )}
              </span>
            </div>

            {/* Tekrar gönder */}
            <div className="vca-otp-info-row">
              <ArrowPathRoundedSquareIcon className="vca-otp-info-ic" aria-hidden="true" />
              <span className="vca-otp-info-key">Tekrar kod gönderme hakkı</span>
              <span className="vca-otp-info-val">
                {smsRemaining > 0 ? (
                  <span className="vca-otp-badge vca-otp-badge--blue">
                    {smsRemaining} kez
                  </span>
                ) : (
                  <span className="vca-otp-badge vca-otp-badge--gray">Hak kalmadı</span>
                )}
              </span>
            </div>

            {/* Cooldown veya hazır */}
            {smsRemaining > 0 ? (
              <div className="vca-otp-info-row">
                <ArrowPathIcon className="vca-otp-info-ic" aria-hidden="true" />
                <span className="vca-otp-info-key">Tekrar gönder</span>
                <span className="vca-otp-info-val">
                  {resendInSec > 0 ? (
                    <span className="vca-otp-timer vca-otp-timer--sm">
                      {formatRemaining(resendInSec)} sonra aktif
                    </span>
                  ) : (
                    <span className="vca-otp-badge vca-otp-badge--green">Hazır</span>
                  )}
                </span>
              </div>
            ) : null}

          </div>

          {/* ── Aksiyonlar ── */}
          <div className="vca-otp-actions">
            <button
              type="button"
              className="vca-otp-btn vca-otp-btn--ghost"
              onClick={onResend}
              disabled={!canResend}
            >
              <ArrowPathRoundedSquareIcon aria-hidden="true" />
              Tekrar Gönder
            </button>

            <button
              type="button"
              className="vca-otp-btn vca-otp-btn--primary"
              onClick={onVerify}
              disabled={loading || String(code || "").trim().length !== 6}
            >
              {loading ? (
                <>
                  <ArrowPathIcon className="spin" aria-hidden="true" />
                  Doğrulanıyor…
                </>
              ) : (
                <>
                  <CheckCircleIcon aria-hidden="true" />
                  Doğrula
                </>
              )}
            </button>
          </div>

          {/* ── Alt not ── */}
          <div className="vca-otp-footnote">
            Kod gelmedi mi? Spam kutunuzu kontrol edin veya tekrar gönder butonunu kullanın.
          </div>

        </div>
      </div>
    </>
  );
}

/* =============================================================================
  Page — DEĞİŞMEDİ (sadece OTP uyarı alanı güncellendi)
============================================================================= */

export default function VatandasCreateApplicationPage() {
  const dispatch = useDispatch();

  const {
    otpEnabled,
    otpConfigStatus,
    otpSessionToken,
    otpExpiresAt,
    otpResendAvailableAt,
    otpSmsRemaining,
    otpStatus,
    otpError,
    otpVerifyStatus,
    otpVerifyError,
  } = useSelector((state) => state.vatandasApplication);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    dispatch(fetchOtpConfig());

    return () => {
      dispatch(resetOtpState());
    };
  }, [dispatch]);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    email: "",
    subject: "",
    description: "",
    unit: "",
    neighborhood: "",
    address: "",
    latitude: null,
    longitude: null,
  });

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [fileError, setFileError] = useState("");
  const [formError, setFormError] = useState("");

  const [units, setUnits] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);

  const [isKvkkAccepted, setIsKvkkAccepted] = useState(false);
  const [showKvkkModal, setShowKvkkModal] = useState(false);
  const [kvkkText, setKvkkText] = useState("");

  const [toast, setToast] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);

  const [trackingNumber, setTrackingNumber] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);

  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [clockNow, setClockNow] = useState(Date.now());

  useEffect(() => {
    if (!otpModalOpen) return;
    const t = window.setInterval(() => setClockNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [otpModalOpen]);

  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 3200);
    return () => window.clearTimeout(t);
  }, [toast]);

  const showToast = useCallback((type, title, message) => {
    setToast({ type, title, message });
  }, []);

  useEffect(() => {
    if (!showSuccess) return;
    const t = window.setTimeout(() => setShowSuccess(false), 10000);
    return () => window.clearTimeout(t);
  }, [showSuccess]);

  useEffect(() => {
    return () => {
      for (const item of selectedFiles) {
        if (item?.isImage && item?.previewUrl) {
          try {
            URL.revokeObjectURL(item.previewUrl);
          } catch {}
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const otpExpiresInSec = useMemo(() => {
    if (!otpExpiresAt) return 0;
    const diff = Math.floor((new Date(otpExpiresAt).getTime() - clockNow) / 1000);
    return Math.max(0, diff);
  }, [otpExpiresAt, clockNow]);

  const otpResendInSec = useMemo(() => {
    if (!otpResendAvailableAt) return 0;
    const diff = Math.floor((new Date(otpResendAvailableAt).getTime() - clockNow) / 1000);
    return Math.max(0, diff);
  }, [otpResendAvailableAt, clockNow]);

  const otpBusy = otpStatus === "loading" || otpVerifyStatus === "loading";

  /* ---------------------------------------------------------------------------
    Data fetch: Units / Neighborhoods — DEĞİŞMEDİ
  --------------------------------------------------------------------------- */
  useEffect(() => {
    const ctrl = new AbortController();

    const run = async () => {
      try {
        const [unitRes, neighRes] = await Promise.all([
          fetch(`${BACKEND_URL}/core/units/`, { signal: ctrl.signal }),
          fetch(`${BACKEND_URL}/core/neighborhoods/`, { signal: ctrl.signal }),
        ]);

        if (!unitRes.ok || !neighRes.ok) throw new Error("Listeler alınamadı");

        const rawUnits = normalizeListPayload(await unitRes.json());
        const publicUnits = rawUnits.filter((u) => u && u.is_public !== false);

        setUnits(publicUnits);
        setNeighborhoods(normalizeListPayload(await neighRes.json()));
      } catch (err) {
        if (err?.name === "AbortError") return;
        console.error("Birim/Mahalle alınamadı:", err);
        showToast("error", "Bağlantı hatası", "Birim/Mahalle listeleri alınamadı. Lütfen tekrar deneyin.");
      }
    };

    run();
    return () => ctrl.abort();
  }, [showToast]);

  /* ---------------------------------------------------------------------------
    KVKK HTML — DEĞİŞMEDİ
  --------------------------------------------------------------------------- */
  useEffect(() => {
    const ctrl = new AbortController();

    fetch("/kvkk/kvkk.html", { signal: ctrl.signal })
      .then((res) => res.text())
      .then((html) => setKvkkText(html))
      .catch((err) => {
        if (err?.name === "AbortError") return;
        console.error("KVKK metni alınamadı:", err);
      });

    return () => ctrl.abort();
  }, []);

  /* ---------------------------------------------------------------------------
    Selected unit -> required flags — DEĞİŞMEDİ
  --------------------------------------------------------------------------- */
  const selectedUnit = useMemo(() => {
    const id = Number(formData.unit);
    if (!id) return null;
    return units.find((u) => Number(u?.id) === id) || null;
  }, [formData.unit, units]);

  const isAddressRequired = !!selectedUnit?.is_address_required;
  const isNeighborhoodRequired = !!selectedUnit?.is_neighborhood_required;
  const isLocationRequired = !!selectedUnit?.is_location_required;
  const isImageRequired = !!selectedUnit?.is_image_required;

  /* ---------------------------------------------------------------------------
    Handlers — DEĞİŞMEDİ
  --------------------------------------------------------------------------- */
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "unit") {
      setFormData((prev) => ({
        ...prev,
        unit: value,
        neighborhood: "",
        address: "",
      }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const locationValue = useMemo(() => {
    if (formData.latitude == null || formData.longitude == null) return null;
    const lat = Number(formData.latitude);
    const lng = Number(formData.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return { lat, lng };
  }, [formData.latitude, formData.longitude]);

  const handleLocationChange = useCallback((payload) => {
    if (!payload) {
      setFormData((prev) => ({ ...prev, latitude: null, longitude: null }));
      return;
    }

    const lat = safeRound6(payload.lat);
    const lng = safeRound6(payload.lng);

    setFormData((prev) => ({
      ...prev,
      latitude: lat,
      longitude: lng,
    }));
  }, []);

  const handleFileChange = (e) => {
    setFileError("");
    setFormError("");

    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const validTypes = ["image/jpeg", "image/png", "image/gif", "application/pdf"];
    const MAX_SIZE = 10 * 1024 * 1024;

    let imageCount = selectedFiles.filter((x) => x.isImage).length;
    let pdfCount = selectedFiles.filter((x) => !x.isImage).length;

    const merged = [...selectedFiles];

    for (const file of files) {
      if (merged.length >= 4) {
        setFileError("En fazla 4 adet dosya yükleyebilirsiniz.");
        break;
      }
      if (file.size > MAX_SIZE) {
        setFileError("Her bir dosya en fazla 10 MB olabilir.");
        continue;
      }
      if (!validTypes.includes(file.type)) {
        setFileError("Geçersiz dosya türü (JPEG, PNG, GIF, PDF).");
        continue;
      }

      const isImg = file.type.startsWith("image/");
      if (isImg && imageCount >= 2) {
        showToast("info", "Resim sınırı", "En fazla 2 resim ekleyebilirsiniz.");
        continue;
      }
      if (!isImg && pdfCount >= 2) {
        showToast("info", "Belge sınırı", "En fazla 2 PDF belge ekleyebilirsiniz.");
        continue;
      }

      const previewUrl = isImg ? URL.createObjectURL(file) : "";
      merged.push({ file, previewUrl, isImage: isImg });

      if (isImg) imageCount += 1;
      else pdfCount += 1;
    }

    setSelectedFiles(merged);
    e.target.value = "";
  };

  const handleRemoveFile = (index) => {
    const updated = [...selectedFiles];
    const item = updated[index];

    if (item?.isImage && item?.previewUrl) {
      try {
        URL.revokeObjectURL(item.previewUrl);
      } catch {}
    }

    updated.splice(index, 1);
    setSelectedFiles(updated);
  };

  const cleanupPreviews = useCallback(() => {
    for (const item of selectedFiles) {
      if (item?.isImage && item?.previewUrl) {
        try {
          URL.revokeObjectURL(item.previewUrl);
        } catch {}
      }
    }
  }, [selectedFiles]);

  const resetFormAfterSuccess = useCallback(() => {
    setFormData({
      firstName: "",
      lastName: "",
      phoneNumber: "",
      email: "",
      subject: "",
      description: "",
      unit: "",
      neighborhood: "",
      address: "",
      latitude: null,
      longitude: null,
    });

    cleanupPreviews();
    setSelectedFiles([]);
    setIsKvkkAccepted(false);
    setOtpCode("");
    setOtpModalOpen(false);
    dispatch(resetOtpState());
  }, [cleanupPreviews, dispatch]);

  const closeOtpModal = useCallback(() => {
    if (otpBusy) return;
    setOtpModalOpen(false);
    setOtpCode("");
    dispatch(resetOtpState());
  }, [dispatch, otpBusy]);

  const buildApplicationFormData = useCallback(() => {
    const appData = new FormData();

    appData.append("subject", formData.subject);
    appData.append("description", formData.description);
    appData.append("unit", formData.unit);

    if (formData.neighborhood) appData.append("neighborhood", formData.neighborhood);
    if (formData.address) appData.append("address", formData.address);

    if (formData.latitude != null && formData.longitude != null) {
      appData.append("latitude", String(safeRound6(formData.latitude)));
      appData.append("longitude", String(safeRound6(formData.longitude)));
    }

    let imageCount = 0;
    let docCount = 0;

    for (const item of selectedFiles) {
      const { file, isImage } = item;
      if (!file) continue;

      if (isImage) {
        if (imageCount === 0) appData.append("image1", file);
        else if (imageCount === 1) appData.append("image2", file);
        imageCount += 1;
      } else {
        if (docCount === 0) appData.append("document1", file);
        else if (docCount === 1) appData.append("document2", file);
        docCount += 1;
      }
    }

    appData.append("citizen.name", formData.firstName);
    appData.append("citizen.surname", formData.lastName);
    appData.append("citizen.phone_number", formData.phoneNumber);
    if (formData.email) appData.append("citizen.email", formData.email);

    return appData;
  }, [formData, selectedFiles]);

  const handleVerifyOtp = async () => {
    try {
      dispatch(clearOtpError());

      if (String(otpCode || "").trim().length !== 6) {
        showToast("info", "Eksik kod", "Lütfen 6 haneli doğrulama kodunu girin.");
        return;
      }

      setIsSubmitting(true);

      const response = await dispatch(
        verifyOtpAndSubmit({
          sessionToken: otpSessionToken,
          code: otpCode,
        })
      ).unwrap();

      if (response?.tracking_number) {
        setTrackingNumber(response.tracking_number);
        setShowSuccess(true);
        resetFormAfterSuccess();
      } else {
        showToast("error", "Beklenmeyen yanıt", "Doğrulama tamamlandı ancak takip numarası alınamadı.");
      }
    } catch (err) {
      const raw =
        (typeof err === "string" && err) ||
        err?.detail ||
        err?.message ||
        "Kod doğrulanamadı. Lütfen tekrar deneyin.";
      const msg = sanitizeErrorMessage(raw) || "Kod doğrulanamadı. Lütfen tekrar deneyin.";
      showToast("error", "Doğrulama başarısız", msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendOtp = async () => {
    try {
      dispatch(clearOtpError());
      await dispatch(resendOtp(otpSessionToken)).unwrap();
      showToast("success", "Yeni kod gönderildi", "Telefonunuza yeni doğrulama kodu gönderildi.");
    } catch (err) {
      const raw =
        (typeof err === "string" && err) ||
        err?.detail ||
        err?.message ||
        "Kod tekrar gönderilemedi.";
      const msg = sanitizeErrorMessage(raw) || "Kod tekrar gönderilemedi.";
      showToast("error", "Gönderilemedi", msg);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    try {
      setFileError("");
      setFormError("");
      setToast(null);

      if (!isKvkkAccepted) {
        showToast("info", "KVKK onayı gerekli", "Devam etmek için KVKK metnini kabul etmelisiniz.");
        return;
      }
      if (!validatePhoneNumber(formData.phoneNumber)) {
        showToast("info", "Telefon hatalı", "Geçerli telefon numarası giriniz (10 hane, 5XXXXXXXXX).");
        return;
      }
      if (!validateEmail(formData.email)) {
        showToast("info", "E-posta hatalı", "Geçerli bir e-posta adresi giriniz.");
        return;
      }

      if (!String(formData.unit || "").trim()) {
        showToast("info", "Eksik bilgi", "İlgili birim seçimi zorunludur.");
        return;
      }
      if (isNeighborhoodRequired && !String(formData.neighborhood || "").trim()) {
        showToast("info", "Eksik bilgi", "Mahalle seçimi zorunludur.");
        return;
      }
      if (isAddressRequired && !String(formData.address || "").trim()) {
        showToast("info", "Eksik bilgi", "Adres alanı zorunludur.");
        return;
      }
      if (isLocationRequired && (formData.latitude == null || formData.longitude == null)) {
        showToast("info", "Eksik bilgi", "Konum seçimi zorunludur.");
        return;
      }
      if (isImageRequired && selectedFiles.length === 0) {
        showToast("info", "Eksik bilgi", "Bu birim için en az 1 dosya yüklemelisiniz.");
        return;
      }

      if (otpConfigStatus === "loading") {
        showToast("info", "Lütfen bekleyin", "Doğrulama yapılandırması yükleniyor.");
        return;
      }

      setIsSubmitting(true);

      const appData = buildApplicationFormData();

      if (!otpEnabled) {
        const response = await dispatch(createApplication(appData)).unwrap();

        if (response?.tracking_number) {
          setTrackingNumber(response.tracking_number);
          setShowSuccess(true);
          resetFormAfterSuccess();
        } else {
          showToast("error", "Beklenmeyen yanıt", "Başvuru kaydedildi ancak takip numarası alınamadı.");
        }

        return;
      }

      const sessionResponse = await dispatch(initOtpSession(appData)).unwrap();

      setOtpCode("");
      setOtpModalOpen(true);

      if (sessionResponse?.sms_failed) {
        showToast(
          "error",
          "SMS gönderilemedi",
          "Doğrulama kodu gönderilemedi. Tekrar gönder seçeneğini kullanabilirsiniz."
        );
      } else {
        showToast(
          "success",
          "Kod gönderildi",
          "Telefonunuza gönderilen doğrulama kodunu girerek başvurunuzu tamamlayın."
        );
      }
    } catch (err) {
      const raw =
        (typeof err === "string" && err) ||
        err?.detail ||
        err?.message ||
        "Başvuru gönderilemedi. Lütfen bilgileri kontrol edip tekrar deneyin.";
      const msg = sanitizeErrorMessage(raw) || "Başvuru gönderilemedi. Lütfen bilgileri kontrol edip tekrar deneyin.";
      setFormError(msg);
      showToast("error", "Gönderilemedi", msg);
    } finally {
      setIsSubmitting(false);
      isSubmittingRef.current = false;
    }
  };

  const handleCopyTracking = useCallback(async () => {
    if (!trackingNumber) return;
    const ok = await copyToClipboard(trackingNumber);
    if (ok) showToast("success", "Kopyalandı", "Takip numarası panoya kopyalandı.");
    else showToast("error", "Kopyalanamadı", "Tarayıcı izin vermedi. Takip numarasını manuel kopyalayın.");
  }, [trackingNumber, showToast]);

  return (
    <div className="vca-page" data-unit-selected={formData.unit ? "1" : "0"}>
      <div className="vca-shell">
        {/* Header */}
        <div className="vca-head v-card vca-head--simple">
          <div className="vca-head-badge" aria-hidden="true">
            <DocumentPlusIcon className="vca-head-badge-ic" />
          </div>
          <h1 className="vca-title vca-title--simple">Başvuru Oluştur</h1>
        </div>

        {/* Page-level error */}
        {formError ? (
          <div className="vca-alert vca-alert--danger" role="alert">
            <ExclamationTriangleIcon className="vca-alert-ic" aria-hidden="true" />
            <div className="vca-alert-text">{sanitizeErrorMessage(formError) || "Bir hata oluştu. Lütfen tekrar deneyin."}</div>
          </div>
        ) : null}

        <form className="vca-form" onSubmit={handleSubmit} noValidate>
          <div className="vca-grid">
            {/* LEFT — form alanları */}
            <div className="vca-col">
              <div className="vca-card v-card">
                <div className="vca-card-title">
                  <UserIcon className="vca-card-title-ic" aria-hidden="true" />
                  <span>Kişisel Bilgiler</span>
                </div>

                <div className="vca-fields">
                  {/* İsim / Soyisim */}
                  <div className="vca-row vca-row--2">
                    <div className="vca-field">
                      <label className="vca-label">
                        Ad <span className="vca-req">*</span>
                      </label>
                      <div className="vca-input-wrap">
                        <UserIcon className="vca-input-ic" aria-hidden="true" />
                        <input
                          className="v-input vca-input"
                          type="text"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleChange}
                          required
                          placeholder="Adınız"
                          disabled={isSubmitting}
                        />
                      </div>
                    </div>

                    <div className="vca-field">
                      <label className="vca-label">
                        Soyad <span className="vca-req">*</span>
                      </label>
                      <div className="vca-input-wrap">
                        <UserIcon className="vca-input-ic" aria-hidden="true" />
                        <input
                          className="v-input vca-input"
                          type="text"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleChange}
                          required
                          placeholder="Soyadınız"
                          disabled={isSubmitting}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Telefon / E-posta */}
                  <div className="vca-row vca-row--2">
                    <div className="vca-field">
                      <label className="vca-label">
                        Telefon <span className="vca-req">*</span>
                      </label>
                      <div className="vca-input-wrap">
                        <PhoneIcon className="vca-input-ic" aria-hidden="true" />
                        <input
                          className="v-input vca-input"
                          type="tel"
                          name="phoneNumber"
                          value={formData.phoneNumber}
                          onChange={handleChange}
                          required
                          placeholder="5XXXXXXXXX"
                          maxLength={10}
                          inputMode="numeric"
                          disabled={isSubmitting}
                        />
                      </div>
                    </div>

                    <div className="vca-field">
                      <label className="vca-label">E-posta</label>
                      <div className="vca-input-wrap">
                        <EnvelopeIcon className="vca-input-ic" aria-hidden="true" />
                        <input
                          className="v-input vca-input"
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="ornek@mail.com"
                          disabled={isSubmitting}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Konu */}
                  <div className="vca-field">
                    <label className="vca-label">
                      Başvuru Konusu <span className="vca-req">*</span>
                    </label>
                    <div className="vca-input-wrap">
                      <TagIcon className="vca-input-ic" aria-hidden="true" />
                      <input
                        className="v-input vca-input"
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        placeholder="Kısa başlık"
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>

                  {/* Açıklama */}
                  <div className="vca-field">
                    <label className="vca-label">
                      Açıklama <span className="vca-req">*</span>
                    </label>
                    <div className="vca-textarea-wrap">
                      <PencilSquareIcon className="vca-input-ic" aria-hidden="true" />
                      <textarea
                        className="v-input vca-textarea"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        required
                        placeholder="Detay"
                        disabled={isSubmitting}
                        rows={5}
                      />
                    </div>
                  </div>

                  {/* Birim */}
                  <div className="vca-field">
                    <label className="vca-label">
                      İlgili Birim <span className="vca-req">*</span>
                    </label>
                    <div className="vca-select-wrap">
                      <Squares2X2Icon className="vca-input-ic" aria-hidden="true" />
                      <select
                        className="v-input vca-select"
                        name="unit"
                        value={formData.unit}
                        onChange={handleChange}
                        required
                        disabled={isSubmitting}
                      >
                        <option value="">Seçiniz</option>
                        {units.map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Mahalle */}
                  {isNeighborhoodRequired ? (
                    <div className="vca-field">
                      <label className="vca-label">
                        Mahalle <span className="vca-req">*</span>
                      </label>
                      <div className="vca-select-wrap">
                        <MapPinIcon className="vca-input-ic" aria-hidden="true" />
                        <select
                          className="v-input vca-select"
                          name="neighborhood"
                          value={formData.neighborhood}
                          onChange={handleChange}
                          required
                          disabled={isSubmitting}
                        >
                          <option value="">Seçiniz</option>
                          {neighborhoods.map((n) => (
                            <option key={n.id} value={n.id}>
                              {n.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  ) : null}

                  {/* Adres */}
                  {isAddressRequired ? (
                    <div className="vca-field">
                      <label className="vca-label">
                        Adres <span className="vca-req">*</span>
                      </label>
                      <div className="vca-input-wrap">
                        <MapPinIcon className="vca-input-ic" aria-hidden="true" />
                        <input
                          className="v-input vca-input"
                          type="text"
                          name="address"
                          value={formData.address}
                          onChange={handleChange}
                          required
                          placeholder="Adres"
                          disabled={isSubmitting}
                        />
                      </div>
                    </div>
                  ) : null}

                  {/* Dosyalar */}
                  <div className="vca-field">
                    <label className="vca-label">
                      Resim / Belge {isImageRequired ? <span className="vca-req">*</span> : null}
                    </label>

                    <div className="vca-uploader">
                      <div className="vca-uploader-row">
                        <div className="vca-uploader-left">
                          <PaperClipIcon className="vca-uploader-ic" aria-hidden="true" />
                          <div className="vca-uploader-text">
                            <div className="vca-uploader-title">Dosya ekle</div>
                            <div className="vca-uploader-sub">En fazla 2 resim + 2 PDF (10 MB)</div>
                          </div>
                        </div>

                        <label className="vca-uploader-btn">
                          <input
                            type="file"
                            multiple
                            onChange={handleFileChange}
                            disabled={isSubmitting}
                            className="vca-file-input"
                            accept="image/jpeg,image/png,image/gif,application/pdf"
                          />
                          <ArrowUpTrayIcon className="vca-uploader-btn-ic" aria-hidden="true" />
                          Seç
                        </label>
                      </div>

                      {selectedFiles.length ? (
                        <div className="vca-previews">
                          {selectedFiles.map((item, idx) => (
                            <div className="vca-preview" key={idx}>
                              <div className="vca-preview-thumb">
                                {item.isImage ? (
                                  <img src={item.previewUrl} alt="Önizleme" className="vca-preview-img" />
                                ) : (
                                  <div className="vca-preview-pdf">
                                    <DocumentTextIcon className="vca-preview-pdf-ic" aria-hidden="true" />
                                    <div className="vca-preview-pdf-txt">PDF</div>
                                  </div>
                                )}
                              </div>

                              <div className="vca-preview-meta">
                                <div className="vca-preview-name" title={item.file?.name}>
                                  {item.file?.name || "Dosya"}
                                </div>
                                <div className="vca-preview-size">{humanFileSize(item.file?.size)}</div>
                              </div>

                              <button
                                type="button"
                                className="vca-preview-del"
                                onClick={() => handleRemoveFile(idx)}
                                disabled={isSubmitting}
                                aria-label="Dosyayı kaldır"
                              >
                                <TrashIcon className="vca-preview-del-ic" aria-hidden="true" />
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : null}

                      {fileError ? <div className="vca-uploader-err">{fileError}</div> : null}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT — konum */}
            <div className="vca-col vca-col--location">
              <div className="vca-card v-card">
                <div className="vca-card-title">
                  <MapPinIcon className="vca-card-title-ic" aria-hidden="true" />
                  <span>
                    Başvuru Konumu {isLocationRequired ? <span className="vca-req">*</span> : null}
                  </span>
                </div>

                <div className="vca-fields">
                  <PreciseLocationPicker
                    value={locationValue}
                    onChange={handleLocationChange}
                    title=""
                    required={false}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* KVKK + Submit */}
          <div className="vca-actions">
            <div className="vca-actions-left">
              <div className="vca-actions-title">
                <ShieldCheckIcon className="vca-actions-title-ic" aria-hidden="true" />
                <span>KVKK Onayı</span>
              </div>

              <label className="vca-kvkk-row">
                <input
                  type="checkbox"
                  checked={isKvkkAccepted}
                  onChange={(e) => setIsKvkkAccepted(e.target.checked)}
                  disabled={isSubmitting}
                />
                <span>
                  <button
                    type="button"
                    className="vca-kvkk-link"
                    onClick={() => setShowKvkkModal(true)}
                    disabled={isSubmitting}
                  >
                    KVKK metnini
                  </button>{" "}
                  okudum ve kabul ediyorum. <span className="vca-req">*</span>
                </span>
              </label>

              {/* OTP etkinse: güncellendi uyarı */}
              {otpEnabled ? (
                <div className="vca-otp-notice">
                  <div className="vca-otp-notice-icon" aria-hidden="true">
                    <DevicePhoneMobileIcon />
                  </div>
                  <div className="vca-otp-notice-text">
                    <strong>Başvurunuz SMS ile doğrulanacaktır.</strong>
                    <span>
                      &nbsp;Gönder butonuna bastıktan sonra telefon numaranıza gelen 6 haneli kodu
                      girmeniz gerekecektir.
                    </span>
                  </div>
                </div>
              ) : null}
            </div>

            <div className="vca-actions-right">
              <button
                type="submit"
                className="v-btn v-btn--primary vca-submit-btn"
                disabled={!isKvkkAccepted || isSubmitting || otpConfigStatus === "loading"}
              >
                {isSubmitting ? (
                  <>
                    <ArrowPathIcon className="vca-submit-ic spin" aria-hidden="true" />
                    Gönderiliyor…
                  </>
                ) : (
                  <>
                    <CheckCircleIcon className="vca-submit-ic" aria-hidden="true" />
                    Gönder
                  </>
                )}
              </button>
            </div>
          </div>
        </form>

        {/* Toast */}
        <Toast toast={toast} onClose={() => setToast(null)} />

        {/* OTP modal */}
        <OtpModal
          open={otpModalOpen}
          phoneNumber={formData.phoneNumber}
          code={otpCode}
          onCodeChange={setOtpCode}
          onVerify={handleVerifyOtp}
          onResend={handleResendOtp}
          onClose={closeOtpModal}
          expiresInSec={otpExpiresInSec}
          resendInSec={otpResendInSec}
          smsRemaining={otpSmsRemaining}
          loading={otpBusy}
          errorText={otpVerifyError || otpError}
        />

        {/* Success modal */}
        <SuccessModal
          open={showSuccess}
          trackingNumber={trackingNumber}
          onClose={() => setShowSuccess(false)}
          onCopy={handleCopyTracking}
        />

        {/* KVKK modal */}
        <KvkkModal
          open={showKvkkModal}
          html={kvkkText}
          onClose={() => setShowKvkkModal(false)}
          onAccept={() => {
            setIsKvkkAccepted(true);
            setShowKvkkModal(false);
            showToast("success", "Onay alındı", "KVKK onayınız kaydedildi.");
          }}
        />
      </div>
    </div>
  );
}